import React, { useContext, useEffect, useState } from "react";
import {
  Button,
  Group,
  Stack,
  Text,
  Image,
  Box,
  Loader,
  Flex,
  Center,
  CopyButton,
  ActionIcon,
  Paper,
  useMantineTheme,
} from "@mantine/core";
import {
  IconBolt,
  IconHeart,
  IconLock,
  IconBuildingBank,
} from "@tabler/icons-react";
import GovUK from "../../../assets/gov_uk_oneid.svg";
import { DataContext } from "../../../context/DataContext";
import { isMobile } from "react-device-detect";
import QRCode from "qrcode.react";
import useStatusCheck from "../../../hooks/useStatusCheck";
import useEdgeStatusCheck from "../../../hooks/useEdgeStatusCheck";

const OneIdStart = () => {
  const { settings, data } = useContext(DataContext);
  const theme = useMantineTheme();
  const [url, setUrl] = useState(null);
  const [requestId, setRequestId] = useState(null);
  
  // Use edge status check when edge is true
  const isEdgeMode = data?.edge === true;
  useStatusCheck(!isEdgeMode, false, "idmax");
  useEdgeStatusCheck(isEdgeMode);

  const handleVerifyClick = () => {
    window.open(url, "_blank");
  };

  // Polling function for edge mode
  const pollForUrl = async (requestId) => {
    const maxAttempts = 100; // 5 minutes = 300 seconds / 3 seconds per attempt
    const pollInterval = 3000; // 3 seconds
    let attempts = 0;

    const poll = async () => {
      try {
        const response = await fetch("https://dcams.app/edge/backend/api/idmax/retrieve", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ request_id: requestId }),
        });

        if (response.ok) {
          const result = await response.json();
          console.log("Poll response:", result);
          
          if (result.request_url) {
            setUrl(result.request_url);
            return; // Success, stop polling
          }
        }

        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, pollInterval);
        } else {
          console.error("Polling timeout: No URL received after 5 minutes");
        }
      } catch (error) {
        console.error("Polling error:", error);
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, pollInterval);
        }
      }
    };

    poll();
  };

  useEffect(() => {
    if (url) return;
    const fetchUrl = async () => {
      // setLoading(true);

      data.provider = "ONEID";
      try {
        // Check if edge mode is enabled  
        let apiUrl;
        let requestData = { ...data };
        
        if (data?.edge === true) {
          apiUrl = process.env.REACT_APP_EDGE_QUEUE_URL;
          // Add queue parameter for idmax
          requestData.queue = "idmax";
        } else {
          apiUrl = process.env.REACT_APP_BACKEND_URL + "/v3/stable/api/frontend/idmax";
        }
        
        const response = await fetch(apiUrl, {
            method: "POST",
            body: JSON.stringify(requestData),
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data_returned = await response.json();
        console.log("data", data_returned);
        
        if (data?.edge === true) {
          // In edge mode, we get a request_id and need to poll for the URL
          const reqId = data_returned.request_id || data.request_id;
          if (reqId) {
            setRequestId(reqId);
            console.log("Starting polling for request_id:", reqId);
            pollForUrl(reqId);
          } else {
            console.error("No request_id found in edge response");
          }
        } else {
          // In normal mode, we get the URL directly
          setUrl(data_returned.continuations.info.template.info.request_url);
        }
      } catch (error) {
        console.error("Error fetching status:", error);
        //setStatus("ERROR"); // or a more descriptive error message
      } finally {
        //setLoading(false);
      }
    };

    fetchUrl();
  }, [url]);

  return (
    <>
      <Box
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between", // This will push the button to the bottom
          height: "100%", // This should take the full height of its parent
        }}
      >
        <Box>
          <Stack>
            <Stack align="center" spacing={0}>
              <Group spacing={6}>
                <IconBuildingBank size={28} />
                <Text fz="xl" fw={700}>
                  Verify With Your Bank
                </Text>
              </Group>

              <Text fz="sm" c="dimmed">
                Powered by OneID®
              </Text>
            </Stack>
            <Center mt="sm">
              <Paper 
                p="lg" 
                withBorder 
                style={{ 
                  backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[6] : "#fafafa" 
                }}
              >
                <Stack>
                  <Group noWrap>
                    <IconBolt size={20} />
                    <Text fz="md">Your bank instantly verifies you</Text>
                  </Group>
                  <Group noWrap>
                    <IconLock size={20} />
                    <Text fz="md">Keep your data safe and secure</Text>
                  </Group>
                  <Group noWrap>
                    <IconHeart size={20} />
                    <Text fz="md">No need to provide documents</Text>
                  </Group>
                </Stack>
              </Paper>
            </Center>
          </Stack>
          {!isMobile && (
            <Stack align="center" mt="xl">
              <Box
                style={{
                  height: 200,
                  width: 200,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Stack>
                  {url ? <QRCode value={url} size={200} /> : <Loader />}
                </Stack>
              </Box>

              <Stack spacing={5}>
                <Text ta="center" fw={700}>
                  Scan to continue
                </Text>
              </Stack>
            </Stack>
          )}
        </Box>
        <Box>
          {!isMobile ? (
            <Group position="center" mt="md" noWrap>
              <Image src={GovUK} width={55} />
              <Text fz="xs">
                OneID® is a Government certified Identity Service Provider.
              </Text>
            </Group>
          ) : (
            <Button
              size="lg"
              mt="lg"
              //leftIcon={<Image src={oneidIconWhite} width={24} />}
              style={{
                backgroundColor: "#041A55",
              }}
              onClick={handleVerifyClick}
              fullWidth
            >
              Get Started
            </Button>
          )}
        </Box>
      </Box>
    </>
  );
};

export default OneIdStart;
